import { useEffect } from "react";
import cx from "classnames";
import { noop } from "es-toolkit/function";
import AssetsFilterEnv from "src/components/features/medium/AssetsFilter/components/AssetsFilterEnv/AssetsFilterEnv";
import { DEFAULT_TASK, FORM_SX } from "../../ComparativeAnalysisForm.constants";
import type { FieldValidationStateType } from "../../ComparativeAnalysisForm.types";
import ComparativeAnalysisFormPipeline from "../ComparativeAnalysisFormPipeline/ComparativeAnalysisFormPipeline";
import ComparativeAnalysisFormPit from "../ComparativeAnalysisFormPit/ComparativeAnalysisFormPit";
import ComparativeAnalysisFormTask from "../ComparativeAnalysisFormTask/ComparativeAnalysisFormTask";

interface ComparativeAnalysisFormGroupProps {
  env?: string;
  setEnv: (env: string) => void;
  pipeline?: string;
  pipelineId?: string;
  setPipeline: (pipeline: string) => void;
  pit?: string;
  setPit: (pit: string) => void;
  task?: string;
  setTask: (task: string | undefined) => void; // task_name-task_id
  className?: string;
  validators?: {
    env: FieldValidationStateType;
    pipeline: FieldValidationStateType;
    pit: FieldValidationStateType;
    task: FieldValidationStateType;
  };
}

const ComparativeAnalysisFormGroup = ({
  env,
  setEnv,
  pipeline,
  pipelineId,
  setPipeline,
  pit,
  setPit,
  task,
  setTask,
  className,
  validators = {
    env: [undefined, noop as (value: boolean | undefined) => void],
    pipeline: [undefined, noop as (value: boolean | undefined) => void],
    pit: [undefined, noop as (value: boolean | undefined) => void],
    task: [undefined, noop as (value: boolean | undefined) => void],
  },
}: ComparativeAnalysisFormGroupProps) => {
  // do not trust the props coming from the search-query-params. validate them (within each field):
  // each field in the group is dependent on the previous field's validity in order to be "enabled"
  const [isValidEnv, setIsValidEnv] = validators.env;
  const [isValidPipeline, setIsValidPipeline] = validators.pipeline;
  const [isValidPit, setIsValidPit] = validators.pit;
  const [isValidTask, setIsValidTask] = validators.task;

  // Reset dependent fields synchronously to prevent child components from
  // rendering with stale values (useEffect runs after render)
  const onEnvChange = (env: string) => {
    setEnv(env);

    // reset fields' values which rely on the env
    setPipeline("");
    setTask(DEFAULT_TASK);
    setPit("");

    // reset validations
    setIsValidPipeline(undefined);
    setIsValidPit(undefined);
    setIsValidTask(undefined);
  };

  // reset fields' values if they are invalid.
  // (best to reset here and not within the fields components)
  // (invalid fields' values might occur if the URL query params were messed with)
  useEffect(() => {
    if (isValidEnv === false) setEnv("");
  }, [isValidEnv, setEnv]);

  useEffect(() => {
    if (isValidPipeline === false || !env) setPipeline("");
  }, [env, isValidPipeline, setPipeline]);

  useEffect(() => {
    // the default "task" has to be the same as in "useComparativeAnalysisFormTask/onLocalChange"
    if (isValidTask === false || !pipeline) setTask(DEFAULT_TASK);
  }, [pit, isValidTask, setTask]);

  useEffect(() => {
    if (isValidPit === false || !pipeline) setPit("");
  }, [pipeline, isValidPit, setPit]);

  return (
    <div className={cx("flex flex-1 gap-1 *:flex-1", className)}>
      <AssetsFilterEnv
        env={env}
        setIsValidEnv={setIsValidEnv}
        onChange={onEnvChange}
        formSx={FORM_SX}
      />

      <ComparativeAnalysisFormPipeline
        env={env}
        pipelineName={pipeline}
        onChange={setPipeline}
        disabled={!isValidEnv}
        setIsValidValue={setIsValidPipeline}
        isLoading={isValidEnv === undefined}
        formSx={FORM_SX}
      />

      <ComparativeAnalysisFormTask
        env={env || ""}
        pipelineName={pipeline || ""}
        pipelineId={pipelineId || ""}
        pit={pit || ""}
        task={task}
        disabled={!isValidPipeline}
        onChange={setTask}
        setIsValidValue={setIsValidTask}
        isLoading={!!pipeline && isValidPipeline === undefined}
        formSx={FORM_SX}
      />

      <ComparativeAnalysisFormPit
        env={env || ""}
        pipelineName={pipeline || ""}
        pipelineId={pipelineId || ""}
        pit={pit}
        task={task}
        disabled={!isValidPipeline}
        onChange={setPit}
        setIsValidValue={setIsValidPit}
        isLoading={!!pipeline && isValidPipeline === undefined}
      />
    </div>
  );
};

export default ComparativeAnalysisFormGroup;
